import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { 
  Activity, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  AlertCircle,
  CheckCircle,
  User,
  Stethoscope,
  Building2,
  Users
} from "lucide-react";
import LogoMikDok from "./components/common/LogoMikDok"; 
const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("patient");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const navigate = useNavigate();

  // Modern styles object
  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    },
    registerCard: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      padding: "48px",
      width: "100%",
      maxWidth: "500px",
      border: "1px solid rgba(255, 255, 255, 0.2)"
    },
    header: {
      textAlign: "center",
      marginBottom: "32px"
    },
    logoContainer: {
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      padding: "16px",
      borderRadius: "20px",
      display: "inline-block",
      marginBottom: "24px",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)"
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      background: "linear-gradient(135deg, #1f2937, #6b7280)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: "0 0 8px 0"
    },
    subtitle: {
      fontSize: "16px",
      color: "#6b7280",
      margin: 0
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "24px"
    },
    inputGroup: {
      position: "relative"
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "8px"
    },
    inputWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center"
    },
    input: {
      width: "100%",
      padding: "16px 16px 16px 48px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "16px",
      transition: "all 0.2s ease",
      backgroundColor: "#f9fafb",
      boxSizing: "border-box",
      outline: "none"
    },
    inputIcon: {
      position: "absolute",
      left: "16px",
      color: "#9ca3af",
      zIndex: 1
    },
    passwordToggle: {
      position: "absolute",
      right: "16px",
      background: "none",
      border: "none",
      color: "#9ca3af",
      cursor: "pointer",
      padding: "4px",
      borderRadius: "4px",
      transition: "color 0.2s ease"
    },
    roleSelector: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px",
      marginTop: "8px"
    },
    roleOption: {
      padding: "16px 12px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      backgroundColor: "#f9fafb",
      cursor: "pointer",
      transition: "all 0.2s ease",
      textAlign: "center",
      position: "relative"
    },
    roleOptionActive: {
      borderColor: "#3b82f6",
      backgroundColor: "#dbeafe",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.2)"
    },
    roleIcon: {
      margin: "0 auto 8px auto",
      padding: "8px",
      borderRadius: "8px",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    roleText: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      margin: 0
    },
    button: {
      width: "100%",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      padding: "16px",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px"
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid transparent",
      borderTop: "2px solid white",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    },
    alert: {
      padding: "16px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "24px"
    },
    errorAlert: {
      backgroundColor: "#fee2e2",
      border: "1px solid #fecaca",
      color: "#dc2626"
    },
    successAlert: {
      backgroundColor: "#dcfce7",
      border: "1px solid #bbf7d0",
      color: "#16a34a"
    },
    footer: {
      textAlign: "center",
      marginTop: "32px",
      padding: "24px 0",
      borderTop: "1px solid #e5e7eb"
    },
    footerText: {
      fontSize: "14px",
      color: "#6b7280",
      margin: "0 0 16px 0"
    },
    linkButton: {
      background: "none",
      border: "none",
      color: "#3b82f6",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      textDecoration: "underline",
      transition: "color 0.2s ease"
    },
    passwordRequirements: {
      fontSize: "12px",
      color: "#6b7280",
      marginTop: "8px",
      padding: "12px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0"
    }
  };

  const roleOptions = [
    {
      value: "patient",
      label: "Patient",
      icon: User,
      description: "Book appointments and manage health records",
      iconBg: "#dbeafe",
      iconColor: "#3b82f6"
    },
    {
      value: "doctor",
      label: "Doctor",
      icon: Stethoscope,
      description: "Provide medical consultations and services",
      iconBg: "#dcfce7",
      iconColor: "#10b981"
    },
    {
      value: "clinic",
      label: "Clinic",
      icon: Building2,
      description: "Manage clinic operations and staff",
      iconBg: "#f3e8ff",
      iconColor: "#8b5cf6"
    }
  ];

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    return {
      minLength,
      hasUpper,
      hasLower,
      hasNumber,
      isValid: minLength && hasUpper && hasLower && hasNumber
    };
  };

  const passwordValidation = validatePassword(password);

  const handleRegister = async () => {
    // Basic validation
    if (!email || !password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!passwordValidation.isValid) {
      setError("Password does not meet requirements");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Create user account
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCred.user;

      // Save user data to Firestore
      await setDoc(doc(db, "users", user.uid), {
        email,
        role,
        createdAt: serverTimestamp(),
        profileComplete: false
      });

      setSuccess("Registration successful! Redirecting to login...");

      // Redirect to login after success
      setTimeout(() => {
        navigate("/login");
      }, 2000);

    } catch (err) {
      console.error("Registration error:", err);
      
      // User-friendly error messages
      let errorMessage = "Registration failed. Please try again.";
      
      if (err.code === "auth/email-already-in-use") {
        errorMessage = "An account with this email already exists. Please sign in instead.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (err.code === "auth/weak-password") {
        errorMessage = "Password is too weak. Please choose a stronger password.";
      } else if (err.code === "auth/operation-not-allowed") {
        errorMessage = "Registration is currently disabled. Please contact support.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
    e.target.style.backgroundColor = "white";
    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.backgroundColor = "#f9fafb";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .register-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4) !important;
        }
        
        .password-toggle:hover {
          color: #374151 !important;
          background-color: #f3f4f6;
        }
        
        .link-button:hover {
          color: #1d4ed8 !important;
        }
        
        .role-option:hover {
          border-color: #60a5fa !important;
          transform: translateY(-1px);
        }
        
        @media (max-width: 640px) {
          .register-card {
            padding: 32px 24px !important;
            margin: 16px !important;
          }
          
          .role-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
        }
      `}</style>
      
      <div style={styles.registerCard} className="register-card">
        {/* Header */}
        <div style={styles.header}>
          
            
  <LogoMikDok
    showText={true}
    subtitle="Register"
    badge={56}
    size={34}
    markColor="#ffffff"
  />


        
          <h1 style={styles.title}>Join MikDok</h1>
          <p style={styles.subtitle}>Create your account to get started</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div style={{...styles.alert, ...styles.errorAlert}}>
            <AlertCircle style={{ width: "16px", height: "16px" }} />
            {error}
          </div>
        )}

        {success && (
          <div style={{...styles.alert, ...styles.successAlert}}>
            <CheckCircle style={{ width: "16px", height: "16px" }} />
            {success}
          </div>
        )}

        {/* Registration Form */}
        <div style={styles.form}>
          {/* Role Selection */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>I am a...</label>
            <div style={styles.roleSelector} className="role-grid">
              {roleOptions.map((option) => {
                const IconComponent = option.icon;
                const isActive = role === option.value;
                
                return (
                  <div
                    key={option.value}
                    style={{
                      ...styles.roleOption,
                      ...(isActive ? styles.roleOptionActive : {})
                    }}
                    className="role-option"
                    onClick={() => setRole(option.value)}
                  >
                    <div style={{
                      ...styles.roleIcon,
                      backgroundColor: isActive ? option.iconColor : option.iconBg,
                    }}>
                      <IconComponent 
                        style={{ 
                          width: "16px", 
                          height: "16px", 
                          color: isActive ? "white" : option.iconColor 
                        }} 
                      />
                    </div>
                    <p style={{
                      ...styles.roleText,
                      color: isActive ? "#1f2937" : "#6b7280"
                    }}>
                      {option.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Email Input */}
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="email">Email Address</label>
            <div style={styles.inputWrapper}>
              <Mail style={{...styles.inputIcon, width: "20px", height: "20px"}} />
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
                disabled={loading}
                required
              />
            </div>
          </div>

          {/* Password Input */}
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="password">Password</label>
            <div style={styles.inputWrapper}>
              <Lock style={{...styles.inputIcon, width: "20px", height: "20px"}} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
                disabled={loading}
                required
              />
              <button
                type="button"
                style={styles.passwordToggle}
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? 
                  <EyeOff style={{ width: "20px", height: "20px" }} /> : 
                  <Eye style={{ width: "20px", height: "20px" }} />
                }
              </button>
            </div>
            
            {/* Password Requirements */}
            {password && (
              <div style={styles.passwordRequirements}>
                <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>Password must contain:</p>
                <ul style={{ margin: 0, paddingLeft: "16px" }}>
                  <li style={{ color: passwordValidation.minLength ? "#10b981" : "#ef4444" }}>
                    At least 8 characters
                  </li>
                  <li style={{ color: passwordValidation.hasUpper ? "#10b981" : "#ef4444" }}>
                    One uppercase letter
                  </li>
                  <li style={{ color: passwordValidation.hasLower ? "#10b981" : "#ef4444" }}>
                    One lowercase letter
                  </li>
                  <li style={{ color: passwordValidation.hasNumber ? "#10b981" : "#ef4444" }}>
                    One number
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password Input */}
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="confirmPassword">Confirm Password</label>
            <div style={styles.inputWrapper}>
              <Lock style={{...styles.inputIcon, width: "20px", height: "20px"}} />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={{
                  ...styles.input,
                  borderColor: confirmPassword && password !== confirmPassword ? "#ef4444" : "#e5e7eb"
                }}
                disabled={loading}
                required
              />
              <button
                type="button"
                style={styles.passwordToggle}
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
              >
                {showConfirmPassword ? 
                  <EyeOff style={{ width: "20px", height: "20px" }} /> : 
                  <Eye style={{ width: "20px", height: "20px" }} />
                }
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p style={{ fontSize: "12px", color: "#ef4444", margin: "4px 0 0 0" }}>
                Passwords do not match
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={handleRegister}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            className="register-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <div style={styles.spinner}></div>
                Creating Account...
              </>
            ) : (
              <>
                Create Account
                <ArrowRight style={{ width: "20px", height: "20px" }} />
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>Already have an account?</p>
          <button 
            style={styles.linkButton}
            className="link-button"
            onClick={() => navigate("/login")}
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default Register;